// Pack 2 line rules and Pack 0 diagonals, under the grid lines: pale lines through cell centres,
// thermo bulbs, between-line circles and lockout diamonds.
import type { ConstraintEntry, PuzzleDoc } from '@zinc/shared';
import { CELL, centre, centresPath, num, ofType, rcList, styleOf } from './geom';
import { BULB_RADIUS, DIAGONAL, ENDS, LINES, THERMO_TIP_GAP, WHITE, type LineStyle } from './style';

/** Line types drawn here; double arrows are drawn with the arrows. */
export const PALE_LINE_TYPES = [
  'thermo',
  'slow-thermo',
  'renban',
  'nabner',
  'whispers',
  'palindrome',
  'region-sum',
  'between',
  'lockout',
  'entropic',
  'modular',
  'parity',
  'zipper',
  'kropki-line',
  'sequence',
  'bulbless-thermo',
  'pointing-thermo',
  'product-line',
  'dominant-line',
  'liar-line',
  'split-thermo',
] as const;
type PaleLine = (typeof PALE_LINE_TYPES)[number];

/** The base style of a line entry: whispers by difference (5 German, 4 Dutch, else custom). */
export function lineStyle(entry: ConstraintEntry): LineStyle {
  const type = entry.type as PaleLine;
  if (type === 'whispers') {
    const d = num(entry, 'difference') ?? 5;
    return d === 5 ? LINES.whispers : d === 4 ? LINES['whispers-dutch'] : LINES['whispers-custom'];
  }
  return LINES[type];
}

function Ends({ entry, color }: { entry: ConstraintEntry; color: string }) {
  const cells = rcList(entry);
  if (cells.length === 0) return null;
  const ends = cells.length === 1 ? [cells[0]!] : [cells[0]!, cells[cells.length - 1]!];
  return (
    <g fill={WHITE} stroke={color} strokeWidth={ENDS.strokeWidth}>
      {ends.map((rc, i) => {
        const [x, y] = centre(rc);
        if (entry.type === 'between') return <circle key={i} cx={x} cy={y} r={ENDS.circle} />;
        const d = ENDS.diamond;
        return (
          <path
            key={i}
            d={`M${x} ${y - d}L${x + d} ${y}L${x} ${y + d}L${x - d} ${y}Z`}
            strokeLinejoin="round"
          />
        );
      })}
    </g>
  );
}

/**
 * Thermos whose tip ends in a cell another thermo also uses (two thermos meeting tip to tip, or
 * a tip touching another thermo). Their tips stop a little short of the centre, so each reads
 * on its own instead of looking like one line with two bulbs. Returns their indices.
 */
export function sharedThermoTips(doc: PuzzleDoc): Set<number> {
  const thermos = ofType(doc, 'thermo', 'slow-thermo').map((e) => rcList(e));
  const out = new Set<number>();
  thermos.forEach((cells, i) => {
    const tip = cells[cells.length - 1];
    if (!tip || cells.length < 2) return;
    const shared = thermos.some(
      (other, j) => j !== i && other.some(([r, c]) => r === tip[0] && c === tip[1]),
    );
    if (shared) out.add(i);
  });
  return out;
}

function shortenedPath(cells: [number, number][]): string {
  const pts = cells.map(centre);
  const [lx, ly] = pts[pts.length - 1]!;
  const [px, py] = pts[pts.length - 2]!;
  const len = Math.hypot(lx - px, ly - py) || 1;
  const gap = THERMO_TIP_GAP;
  pts[pts.length - 1] = [lx - ((lx - px) / len) * gap, ly - ((ly - py) / len) * gap];
  return pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x} ${y}`).join('');
}

export function Lines({ doc }: { doc: PuzzleDoc }) {
  const thermoIndex = new Map(ofType(doc, 'thermo', 'slow-thermo').map((e, k) => [e, k]));
  const shortTips = sharedThermoTips(doc);
  return (
    <g fill="none" strokeLinecap="round" strokeLinejoin="round">
      {ofType(doc, ...PALE_LINE_TYPES).map((e, i) => {
        const cells = rcList(e);
        if (cells.length === 0) return null;
        const { color, width } = styleOf(e, lineStyle(e));
        const thermo =
          e.type === 'thermo' ||
          e.type === 'slow-thermo' ||
          e.type === 'pointing-thermo' ||
          e.type === 'split-thermo';
        const short = thermo && shortTips.has(thermoIndex.get(e) ?? -1);
        const [bx, by] = centre(cells[0]!);
        return (
          <g key={i} data-constraint={e.type} data-short-tip={short ? 'true' : undefined}>
            {cells.length > 1 && (
              <path
                d={short ? shortenedPath(cells) : centresPath(cells)}
                stroke={color}
                strokeWidth={width}
              />
            )}
            {thermo && (
              <circle
                cx={bx}
                cy={by}
                r={BULB_RADIUS * Math.max(1, width / LINES.thermo.width)}
                fill={color}
              />
            )}
            {(e.type === 'between' || e.type === 'lockout') && (
              <Ends entry={e} color={ENDS.color} />
            )}
          </g>
        );
      })}
    </g>
  );
}

/** Positive ("/", bottom-left to top-right) and negative ("\\") diagonals. */
export function Diagonals({ doc }: { doc: PuzzleDoc }) {
  const w = doc.grid.cols * CELL;
  const h = doc.grid.rows * CELL;
  return (
    <g>
      {ofType(doc, 'diagonal', 'anti-diagonal').map((e, i) => {
        const { color, width } = styleOf(e, DIAGONAL);
        const d = e.direction === 'positive' ? `M0 ${h}L${w} 0` : `M0 0L${w} ${h}`;
        return (
          <path
            key={i}
            data-constraint={e.type}
            data-direction={e.direction === 'positive' ? 'positive' : 'negative'}
            d={d}
            stroke={color}
            strokeWidth={width}
            strokeLinecap="round"
          />
        );
      })}
    </g>
  );
}
